import { useEffect, useState } from "react";

import { useCart, useRoofingEstimator } from "../../../hooks/query";
import type { MaterialRowItem } from "../utils/materialLineItems";
import {
    buildMaterialEditsFromRows,
    mapMaterialLineItemsResponse,
} from "../utils/materialLineItems";
import { hydrateMaterialsFromCart } from "../utils/cartMapping";

import { useEstimateContractContextManager } from "./";

export default function useMaterialsInit(projectId?: string) {
    const { setValues, setReferenceValue } = useEstimateContractContextManager();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [materialsByGroup, setMaterialsByGroup] = useState<
        Record<string, MaterialRowItem[]>
    >({});

    const { useMaterialLineItems } = useRoofingEstimator();
    const materialLineItemsSvc = useMaterialLineItems();
    const { useListByProjectId } = useCart();
    const listCartSvc = useListByProjectId();

    const loadMaterialsData = async (
        id: string,
        isCancelled?: () => boolean,
    ) => {
        setLoading(true);
        setError(null);
        try {
            const data = await materialLineItemsSvc.mutateAsync({
                ProjectId: id,
            });
            if (isCancelled?.()) return { success: false };

            const mapped = mapMaterialLineItemsResponse(data);
            setMaterialsByGroup(mapped);

            const baseEdits = buildMaterialEditsFromRows(mapped);
            let materials = baseEdits;
            let cartIds: Record<string, string> = {};

            if (Object.keys(baseEdits).length > 0) {
                const cartItems = await listCartSvc.mutateAsync({
                    ProjectId: id,
                });
                if (isCancelled?.()) return { success: false };

                const hydrated = hydrateMaterialsFromCart(
                    Array.isArray(cartItems) ? cartItems : [],
                    mapped,
                    baseEdits,
                );
                materials = hydrated.materials;
                cartIds = hydrated.cartIds;
            }

            if (isCancelled?.()) return { success: false };

            const applyLoadedMaterials = (prev: any) => ({
                ...prev,
                materials,
                cartIds: {
                    ...(prev.cartIds || {}),
                    ...cartIds,
                },
            });

            setValues(applyLoadedMaterials);
            setReferenceValue(applyLoadedMaterials);

            return { success: true };
        } catch (err: any) {
            if (!isCancelled?.()) {
                setError(err.message || "Failed to load materials");
            }
            return { success: false, error: err };
        } finally {
            if (!isCancelled?.()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!projectId) return;

        let cancelled = false;
        loadMaterialsData(projectId, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [projectId]);

    return {
        loadMaterialsData,
        materialsByGroup,
        loading,
        error,
    };
}
